import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import {
    BRANCH_LIST_MAX_PAGE_SIZE,
    type BranchListQuery,
} from '@/api/handlers/resources/branches/requests';
import type { GetBranchDetailsResponse } from '@/api/handlers/resources/branches/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { BranchQueryKeys } from './branchQueryKeys';

/**
 * Consumers that just need the whole branch set (dropdowns, label lookups)
 * call this with no query → request the largest page the API allows. The
 * Branches management table passes an explicit page/pageSize/search instead.
 * Module-level so the no-arg key stays referentially stable across renders.
 */
const ALL_BRANCHES_QUERY: BranchListQuery = {
    page: 1,
    pageSize: BRANCH_LIST_MAX_PAGE_SIZE,
};

/**
 * The same whole set, archived branches included — for resolving the *name* of
 * a branch something already points at, which the picker list cannot do
 * because it deliberately omits archived rows (bos-api BOS-122). Module-level
 * for the same reason: a stable key.
 */
export const ALL_BRANCHES_WITH_ARCHIVED_QUERY: BranchListQuery = {
    page: 1,
    pageSize: BRANCH_LIST_MAX_PAGE_SIZE,
    includeArchived: true,
};

export const useBranchDetailsQuery = (
    query: BranchListQuery = ALL_BRANCHES_QUERY,
    enabled = true,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetBranchDetailsResponse>>({
        queryKey: BranchQueryKeys.detailsPage(query),
        queryFn: () => apiClient.branches.getBranchDetails(query),
        enabled,
        staleTime: 60_000,
        // Keep the current page on screen while the next one loads so paging
        // doesn't flash the skeleton.
        placeholderData: keepPreviousData,
    });
};
